if (!customElements.get('gf-colour-preference')) {
  customElements.define('gf-colour-preference', class extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.controller = new AbortController();
      this.form = this.closest('form');
      if (!this.form) return;
      this.excluded = new Set((this.dataset.excludedVariants || '').split(',').filter(Boolean));
      this.confirmation = this.querySelector('[data-colour-confirmation]');
      this.preference = this.querySelector('[data-colour-preference]');
      const options = { signal: this.controller.signal };
      this.form.addEventListener('change', () => this.sync(), options);
      this.form.addEventListener('submit', () => this.sync(), { ...options, capture: true });
      this.form.addEventListener('formdata', event => {
        const eligible = this.sync();
        if (!this.confirmation) return;
        if (eligible) event.formData.set(this.confirmation.name, this.confirmation.value);
        else event.formData.delete(this.confirmation.name);
        if (this.preference) {
          const value = this.preference.value.trim();
          if (eligible && value) event.formData.set(this.preference.name, value);
          else event.formData.delete(this.preference.name);
        }
      }, options);
      this.sync();
    }

    disconnectedCallback() {
      this.controller?.abort();
    }

    sync() {
      const variant = this.form?.querySelector('[name="id"]');
      const eligible = !!variant?.value && !this.excluded.has(String(variant.value));
      this.hidden = this.hasAttribute('data-status-only') || !eligible;
      if (this.confirmation) this.confirmation.disabled = !eligible;
      if (this.preference) this.preference.disabled = !eligible;
      return eligible;
    }
  });
}

if (!customElements.get('gf-fabric-swatches')) {
  customElements.define('gf-fabric-swatches', class extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.controller = new AbortController();
      this.trigger = this.querySelector('[data-swatch-open]');
      this.template = this.querySelector('[data-swatch-template]');
      if (!this.trigger || !this.template || !window.HTMLDialogElement?.prototype.showModal) return;
      this.trigger.addEventListener('click', event => {
        event.preventDefault();
        this.open();
      }, { signal: this.controller.signal });
    }

    disconnectedCallback() {
      if (this.dialog?.open) this.dialog.close();
      this.unlockScroll();
      this.controller?.abort();
      this.resizeObserver?.disconnect();
    }

    prepare() {
      if (this.dialog) return;
      this.append(this.template.content.cloneNode(true));
      this.dialog = this.querySelector('dialog');
      this.image = this.querySelector('[data-swatch-image]');
      this.viewport = this.querySelector('[data-swatch-viewport]');
      this.status = this.querySelector('[data-swatch-status]');
      this.caption = this.querySelector('[data-swatch-caption]');
      this.fullSize = this.querySelector('[data-swatch-full]');
      this.scaleOutput = this.querySelector('[data-swatch-scale]');
      this.zoomOut = this.querySelector('[data-swatch-zoom="-1"]');
      this.zoomIn = this.querySelector('[data-swatch-zoom="1"]');
      this.choices = [...this.querySelectorAll('[data-swatch-choice]')];
      const options = { signal: this.controller.signal };
      this.querySelector('[data-swatch-close]').addEventListener('click', () => this.dialog.close(), options);
      this.dialog.addEventListener('cancel', event => { event.preventDefault(); this.dialog.close(); }, options);
      this.dialog.addEventListener('close', () => {
        this.unlockScroll();
        this.trigger.setAttribute('aria-expanded', 'false');
        if (this.trigger.isConnected && !this.closest('[hidden]')) this.trigger.focus({ preventScroll: true });
      }, options);
      this.dialog.addEventListener('click', event => {
        if (event.target !== this.dialog) return;
        const rect = this.dialog.getBoundingClientRect();
        if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) this.dialog.close();
      }, options);
      this.choices.forEach((button, index) => button.addEventListener('click', () => this.select(index), options));
      this.zoomIn.addEventListener('click', () => this.zoom(0.5), options);
      this.zoomOut.addEventListener('click', () => this.zoom(-0.5), options);
      this.dialog.addEventListener('keydown', event => {
        if (event.key === 'Tab') {
          const controls = [...this.dialog.querySelectorAll('button:not(:disabled), a[href]')].filter(control => control.getClientRects().length);
          const first = controls[0];
          const last = controls[controls.length - 1];
          if (event.shiftKey && (document.activeElement === first || document.activeElement === this.querySelector('h2'))) {
            event.preventDefault();
            last?.focus({ preventScroll: true });
          } else if (!event.shiftKey && document.activeElement === last) {
            event.preventDefault();
            first?.focus({ preventScroll: true });
          }
        }
        if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
          event.preventDefault();
          const direction = event.key === 'ArrowRight' ? 1 : -1;
          this.select((this.index + direction + this.choices.length) % this.choices.length);
        }
        if (event.key === '+' || event.key === '=') { event.preventDefault(); this.zoom(0.5); }
        if (event.key === '-') { event.preventDefault(); this.zoom(-0.5); }
      }, options);
      this.image.addEventListener('load', () => {
        if (!this.dialog.open) return;
        this.status.textContent = '';
        this.image.hidden = false;
        this.fit();
      }, options);
      this.image.addEventListener('error', () => {
        this.image.hidden = true;
        this.status.textContent = this.dataset.error;
        this.zoomIn.disabled = this.zoomOut.disabled = true;
      }, options);
      this.resizeObserver = new ResizeObserver(() => { if (this.dialog.open && !this.image.hidden) this.fit(); });
      this.resizeObserver.observe(this.viewport);
    }

    open() {
      if (document.querySelector('dialog[open]')) return;
      this.prepare();
      if (!this.choices.length) return;
      this.lockScroll();
      try { this.dialog.showModal(); }
      catch { this.unlockScroll(); window.open(this.trigger.href, '_blank', 'noopener'); return; }
      this.trigger.setAttribute('aria-expanded', 'true');
      this.select(0);
      this.querySelector('h2').focus({ preventScroll: true });
    }

    select(index) {
      const button = this.choices[index];
      if (!button) return;
      this.index = index;
      this.scale = 1;
      this.image.hidden = true;
      this.status.textContent = this.dataset.loading;
      this.choices.forEach((choice, i) => choice.setAttribute('aria-pressed', String(i === index)));
      this.caption.textContent = button.dataset.swatchLabel || button.dataset.swatchAlt;
      this.fullSize.href = button.dataset.swatchUrl;
      this.image.alt = button.dataset.swatchAlt;
      this.image.width = Number(button.dataset.swatchWidth) || 1200;
      this.image.height = Number(button.dataset.swatchHeight) || 1200;
      this.image.src = button.dataset.swatchUrl;
      this.viewport.scrollTo({ top: 0, left: 0, behavior: 'instant' });
      this.zoomIn.disabled = this.zoomOut.disabled = true;
      this.scaleOutput.textContent = '100%';
      if (this.image.complete && this.image.naturalWidth) {
        this.status.textContent = '';
        this.image.hidden = false;
        this.fit();
      }
    }

    fit() {
      if (!this.image.naturalWidth || !this.image.naturalHeight) return;
      const width = Math.max(80, this.viewport.clientWidth - 28);
      const height = Math.max(80, this.viewport.clientHeight - 28);
      const fit = Math.min(width / this.image.naturalWidth, height / this.image.naturalHeight, 1);
      this.image.style.width = `${Math.round(this.image.naturalWidth * fit * this.scale)}px`;
      this.image.style.height = `${Math.round(this.image.naturalHeight * fit * this.scale)}px`;
      this.scaleOutput.textContent = `${Math.round(this.scale * 100)}%`;
      this.zoomOut.disabled = this.scale <= 1;
      this.zoomIn.disabled = this.scale >= 3;
    }

    zoom(amount) {
      if (this.image.hidden) return;
      this.scale = Math.min(3, Math.max(1, this.scale + amount));
      this.fit();
    }

    lockScroll() {
      if (this.scrollState) return;
      this.scrollState = {
        x: window.scrollX, y: window.scrollY,
        rootOverflow: document.documentElement.style.overflow,
        bodyOverflow: document.body.style.overflow,
        position: document.body.style.position,
        top: document.body.style.top,
        width: document.body.style.width
      };
      document.documentElement.style.overflow = 'hidden';
      Object.assign(document.body.style, { overflow: 'hidden', position: 'fixed', top: `-${this.scrollState.y}px`, width: '100%' });
    }

    unlockScroll() {
      if (!this.scrollState) return;
      const state = this.scrollState;
      document.documentElement.style.overflow = state.rootOverflow;
      Object.assign(document.body.style, { overflow: state.bodyOverflow, position: state.position, top: state.top, width: state.width });
      this.scrollState = null;
      window.scrollTo({ left: state.x, top: state.y, behavior: 'instant' });
    }
  });
}
